/**
 * Centralized English copy for the Inventory page (future i18n: replace with t()).
 */
export const inventoryCopy = {
  page: {
    title: 'Inventory',
    titleAnalytics: 'Inventory Analytics',
    subtitleProducts: "View and manage your organization's card inventory",
    subtitleAnalytics: 'Deep dive into your inventory performance and value distribution',
  },
  search: {
    // One box for everything (Phase C1): the old name + number pair is gone, and a scanned
    // barcode or a typed SKU lands here too.
    placeholder: 'Search name, set, number, SKU or scan a barcode…',
    ariaLabel: 'Search inventory',
    title:
      'Every word must match. Narrow with game: set: cond: printing: kind: channel: stock: sync: status: price>20 — ' +
      'press Enter to turn them into filters. Quote names with spaces: set:"Surging Sparks".',
    statusSearching: 'Searching…',
    statusUpdating: 'Updating results…',
  },
  browse: {
    toggle: 'Browse by set',
  },
  sort: {
    ariaLabel: 'Sort inventory',
  },
  filters: {
    button: 'Filters',
    hint: 'Narrow by game or set',
    clear: 'Clear',
    clearAll: 'Clear All Filters',
  },
  selection: {
    selectedToggle: (n: number) => `${n} Selected`,
    openActions: 'Open actions',
    hideActions: 'Hide actions',
    clear: 'Clear',
    clearSelection: 'Clear selection',
    selectThisPage: (n: string) => `Select this page (${n})`,
    selectAllMatching: 'Select all matching',
    selectAllMatchingHint:
      'Selection is remembered across pages. “Select all matching” calls a reserved API with your current filters; full server-side selection is still in progress.',
    otherPagesSelected: (n: string) => `${n} on other pages`,
    quickActionsHint:
      'Quick actions live here. Use this panel to work with the current selection or select everything on this page.',
  },
  batch: {
    delete: {
      confirmRest: (n: number, samples: string) =>
        `to permanently remove ${n} product${n === 1 ? '' : 's'}. This cannot be undone. Sample: ${samples}`,
      liveRegion: (n: number, samples: string) =>
        `${n} product${n === 1 ? '' : 's'} selected for permanent deletion. Sample names: ${samples}. Type DELETE in the field to confirm.`,
      placeholder: 'DELETE',
      inputAria: 'Type DELETE to confirm',
    },
  },
  card: {
    noImage: 'No Image',
    onSale: 'ON SALE',
    sale: 'SALE',
    manual: 'MANUAL',
    custom: 'CUSTOM',
    event: 'EVENT',
    removedAt: 'Removed',
    preorder: 'PRE-ORDER',
    earlyRelease: 'EARLY',
    draft: 'DRAFT',
    hidden: 'HIDDEN',
    channels: 'CHANNELS',
    releases: 'Releases',
    quantity: 'Quantity',
    total: 'Total:',
    available: 'Available:',
    reserved: 'Reserved:',
    pricing: 'Pricing',
    marketPrice: 'Market Price:',
    listedPrice: 'Listed Price:',
    purchaseCost: 'Purchase Cost:',
    costConverted: (usd: string, rate: number) =>
      `What you paid, recorded as US$${usd} and converted at this listing's exchange rate (${rate})`,
    markupIncluded: (percent: number) => `Includes your ${percent}% platform markup`,
    manualPrice: 'Manual Price:',
    saleLabel: 'Sale:',
    totalValue: 'Total Value',
    priceUnavailable: 'Price unavailable',
    floorActive: 'Floor active',
    market: 'Market',
    minimum: 'Minimum',
    basedOnMarket: 'Based on market',
    basedOnManual: 'Based on manual',
    pricingInsights: 'Pricing & Insights',
    edit: 'Edit',
    open: 'Open product details',
    consigned: 'Consigned',
    setConsignment: 'Consignment',
    removeSale: 'Remove Sale',
    removeManual: 'Remove Manual',
    report: 'Report',
    sku: 'SKU:',
  },
  sales: {
    lastSold: 'Last sold',
    neverSold: 'No sale on record',
    unitsAllTime: 'Units sold',
    unitsInWindow: (days: number) => `Sold in ${days} days`,
    unitsShort: (days: number) => `Sold (${days}d)`,
    lastPrice: 'Last sale price',
    listedNow: 'Listed now',
    restock: 'Restock',
    restockTitle: 'Add stock or change the price',
    held: 'HELD FOR ORDERS',
    heldTitle: (units: number) => `${units} on the shelf, held for open orders. Nothing is free to sell.`,
    soldOutTitle: 'Sold out',
    soldOutBody:
      'Products you stocked that have nothing left to sell. Held for orders means the units are still on the shelf, waiting on an open order.',
    recentTitle: 'Recent sales',
    recentBody: (days: number) => `What sold in the last ${days} days across every channel, after refunds.`,
    restockListTitle: 'Sold out and recently sold',
    restockListBody: (days: number) => `Sold in the last ${days} days with nothing left to sell, after refunds.`,
    windowLabel: 'Sales window',
    empty: (days: number | null) =>
      days !== null
        ? { title: `No sales in the last ${days} days`, body: 'Try a longer window, or clear a filter.' }
        : { title: 'Nothing is sold out', body: 'Every product matching your filters still has stock to sell.' },
  },
} as const
